import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { KeyboardArrowRight, KeyboardArrowLeft, Close } from '@material-ui/icons';
import { isMobileSafari } from 'react-device-detect';
import classnames from 'classnames';

import callApi from '../../../utils/callApi';
import { ANCHOR_PRIVATE_GALLERY, ANCHOR_VIEW_PURCHASED_CLIPSTORE_GALLERY } from '../../../constants/apiUrls/apiUrls';
import collections from '../../../assets/icons/collections_white.png';
import defaultGalleryImage from '../../../assets/default-gallery.png';
import NotificationStore from '../../../store/NotificationStore';
import PayPerViewStore from '../../../store/PayPerViewStore';
import './PrivateGallery.css';
import { anchorIsNakedNewsLive, buildAnchorCheckoutLink, buildAnchorLink } from '../../../utils/helpers';

import { linkToPaymentPage, LockIcon, PriceForClipStoreItem } from './PrivateContentComponents/PrivateContentUtils';
import PrivateContentDescription from './PrivateContentComponents/PrivateContentDescription';


const PrivateGallery = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [currentBodyScrollTop, setCurrentBodyScrollTop] = useState(0);
  const [anchorShortUrl, setAnchorShortUrl] = useState('');
  const [shouldBlurContent, setShouldBlurContent] = useState(true);
  const [userCanAccessContent, setUserCanAccessContent] = useState(false);
  const [isBanned, setIsBanned] = useState(false);

  const {
    gallery,
    subscribed,
    anchorId,
    title,
    imageCount,
    coverPhoto,
    showDescription = true,
    liveEvents = false,
    typeOfListing,
    price,
    payPerViewNav,
    customPriceForFanPlus,
    anchors,
    isClipStorePage,
    isPurchasedTab,
    user,
  } = props;

  const image = coverPhoto || defaultGalleryImage;

  const setBlurringForContent = useCallback((currentAnchor) => {
    const id = currentAnchor?.id || anchorId;
    if (anchorIsNakedNewsLive(id) || typeOfListing === 'fanzone' || isPurchasedTab) {
      setShouldBlurContent(false);
    }
  }, [anchorId]);

  const setBanStatus = useCallback((anchor) => {
    const { anchorSubscriptions } = user?.user?.account || {};
    const anchorID = anchor?.id || anchorId;
    if (anchorSubscriptions) {
      const subscribedToAnchor = anchorSubscriptions.filter(anchorData => parseInt(anchorData.subCategory, 10) === anchorID);
      const userIsBanned = subscribedToAnchor?.[0]?.isBanned;
      setIsBanned(userIsBanned);
    }
  }, [anchorId, user?.user?.account]);

  useEffect(() => {
    const currentAnchor = anchors?.find((anchor) => anchor.id === anchorId);
    setBlurringForContent(currentAnchor);
    setBanStatus(currentAnchor);
    setAnchorShortUrl(buildAnchorLink({
      id: anchorId,
      name: currentAnchor?.name,
    }));
  }, [anchorId, anchors, setBanStatus, setBlurringForContent]);

  const checkIfUserCanAccessContent = useCallback(() => {
    const isClipStoreTab = payPerViewNav === 'New' && typeOfListing === 'clipstore';
    const { authenticated } = user || {};
    if (!isBanned && ((isClipStorePage || isClipStoreTab) && authenticated)) {
      return true;
    }

    return false;
  }, [isBanned, isClipStorePage, payPerViewNav, typeOfListing, user]);

  useEffect(() => {
    setUserCanAccessContent(checkIfUserCanAccessContent());
  }, [checkIfUserCanAccessContent]);

  const formatUrl = useCallback((url, galleryID) =>
    url.replace('{id}', encodeURIComponent(anchorId)).
      replace('{galleryId}', encodeURIComponent(galleryID)), [anchorId]);

  const getUrlForClip = useCallback(() => {
    if (typeOfListing !== 'clipstore') {
      return ANCHOR_PRIVATE_GALLERY;
    }

    return ANCHOR_VIEW_PURCHASED_CLIPSTORE_GALLERY;
  }, [typeOfListing]);

  const getGalleryID = useCallback(() => {
    if (!PayPerViewStore.newItemWasPurchased) {
      return gallery.id;
    }

    return PayPerViewStore.purchasedClip.id;
  }, [gallery.id]);

  const fetchClip = useCallback((url) => {
    callApi(url, 'GET', undefined, undefined, undefined, true, true, true).
      then((res) => {
        setPhotos(res.data);
        PayPerViewStore.setNewItemWasPurchased(false);
      }).
      catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const { selectedClipPrice } = props || {};
    const url = formatUrl(getUrlForClip(), getGalleryID());
    if (isOpen) {
      fetchClip(url);
    }
    if (PayPerViewStore.newItemWasPurchased &&
      selectedClipPrice.id === gallery.id) {
      fetchClip(url);
      setIsOpen(true);
    }

  }, [
    gallery.id,
    isOpen,
    typeOfListing,
    anchorId,
    getUrlForClip,
    getGalleryID,
    formatUrl,
    fetchClip,
    props,
  ]);

  const imagePreviewContentRef = useRef();
  const sliderRef = useRef();

  useEffect(() => {
    if (isOpen) {
      imagePreviewContentRef.current.focus();
      if (isMobileSafari) {
        setCurrentBodyScrollTop(window.scrollY);
        document.body.style.position = 'fixed';
      } else {
        document.body.style.overflow = 'hidden';
      }
    } else if (isMobileSafari) {
      document.body.style.position = 'static';
      if (currentBodyScrollTop) {
        window.scroll(0, currentBodyScrollTop);
      }
    } else {
      document.body.style.overflow = 'visible';
    }
  }, [currentBodyScrollTop, isOpen]);


  const imagePreviewContentClickHandler = useCallback((event) => {
    const { target } = event.nativeEvent;
    if (target.tagName === 'DIV') {
      setIsOpen(false);
    }
  }, []);

  const getGalleryItemLink = useCallback(() => {
    const { isPurchasedTab, isClipStorePage } = props;
    const canViewContent = userCanAccessContent || subscribed;
    if ((isPurchasedTab || (typeOfListing === 'clipstore' && subscribed)) && !isClipStorePage) {
      return '?filter=clipstore';
    } else if (canViewContent) {
      return '#';
    }

    return linkToPaymentPage(subscribed, anchorId, anchorShortUrl);
  }, [props, userCanAccessContent, subscribed, typeOfListing, anchorId, anchorShortUrl]);

  const sentToAcordingLink = useCallback(() => {
    if (userCanAccessContent || subscribed) {
      return `/${anchorShortUrl}`;
    }

    return buildAnchorCheckoutLink(anchorId, props.anchorShortUrl);
  }, [anchorId, anchorShortUrl, props.anchorShortUrl, subscribed, userCanAccessContent]);

  const imagePreviewContentKeyDownHandler = useCallback((event) => {
    switch (event.keyCode) {
      case 27:
        setIsOpen(false);
        break;
      case 39:
        sliderRef.current.slickNext();
        break;
      case 37:
        sliderRef.current.slickPrev();
        break;
      default:
        break;
    }
  }, []);

  const openClipStorePaymentModal = useCallback(() => {
    props.openPayPerViewModal(price, title, gallery.id, gallery.type, customPriceForFanPlus);
    PayPerViewStore.setCurrentlySelectedAnchor(gallery.anchorName);
    PayPerViewStore.setCurrentlySelectedAnchorID(gallery.anchorId);
  }, [customPriceForFanPlus, gallery.anchorId, gallery.anchorName, gallery.id, gallery.type, price, props, title]);

  const handleContentClick = useCallback(() => {
    const isClipStoreTab = payPerViewNav === 'New' && typeOfListing === 'clipstore';
    const shouldShowClipStorePaymentModal = !isBanned && isClipStoreTab;

    if (isBanned) {
      NotificationStore.error('Banned from following this model. Please contact support.');
    } else if (userCanAccessContent && shouldShowClipStorePaymentModal) {
      openClipStorePaymentModal();
    } else {
      setIsOpen((prevValue) => !prevValue);
    }
  }, [isBanned, openClipStorePaymentModal, payPerViewNav, typeOfListing, userCanAccessContent]);

  const isLocked = (!subscribed || isBanned) && typeOfListing !== 'clipstore';
  const leftTitle = `Gallery, ${imageCount} ${imageCount === 1 ? 'picture' : 'pictures'}`;

  return (
    <div className="content-item-container">
      {liveEvents ? (
        <Link
          className="content-item gallery-item"
          to={sentToAcordingLink()}
        >
          <div>
            <img
              alt="private-img"
              className={classnames({ 'blur': shouldBlurContent })}
              src={image}
              style={{ height: '400px', objectFit: 'contain' }}
            />
            <div className="private-gallery-collection-icon">
              <img alt="Collection" src={collections} />
            </div>
            <LockIcon isLocked={isLocked} />
          </div>
        </Link>
      ) : (
        <Link
          className="content-item gallery-item"
          to={getGalleryItemLink()}
          onClick={handleContentClick}
        >

          <div
            className="content-item-thumbnail private-gallery-thumbnail"
          >
            <div
              className={classnames({
                'private-gallery-content-preview': true,
                'blur': shouldBlurContent,
              })}
              style={{
                background: `url(${image}) 
                            no-repeat top center / 
                            ${image ? 'cover' : 'contain'}`,
                backgroundColor: `${gallery.mainImage ? '' : '#a6a6a6'}`,
              }}
            />
            <div className="private-gallery-content-after-overlay" />
            <div className="private-gallery-collection-icon">


              <div className="private-gallery-details">
                <img alt="Collection" src={collections} />
                <PriceForClipStoreItem
                  payPerViewNav={payPerViewNav}
                  price={price}
                />
              </div>
            </div>
            <LockIcon
              isLocked={isLocked}
            />
            {
              <div className="private-gallery-count-icon">
                <div className="private-gallery-lock-container">
                  <img alt="Lock" src={collections} /><div>{imageCount > 9 ? '9+' : imageCount}</div>
                </div>
              </div>
            }
          </div>
          <PrivateContentDescription
            leftTitle={leftTitle}
            media={gallery}
            shouldShowDescription={showDescription}
            title={title}
          />
        </Link>
      )}
      <div
        className="image-preview-container"
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        <div className="close-icon" onClick={() => setIsOpen(false)}>
          <Close fontSize="inherit" />
        </div>
        <div
          ref={imagePreviewContentRef}
          className="image-preview-content"
          tabIndex={0}
          onClick={imagePreviewContentClickHandler}
          onKeyDown={imagePreviewContentKeyDownHandler}
        >
          {photos.images &&
            <Slider
              ref={sliderRef}
              autoplay={false}
              dots={false}
              nextArrow={<KeyboardArrowRight fontSize="inherit" />}
              prevArrow={<KeyboardArrowLeft fontSize="inherit" />}
            >
              {photos.images.map((image) => (
                <div key={image.url}>
                  <img alt="" src={image.url} />
                </div>
              ))}
            </Slider>
          }
        </div>
      </div>
    </div>
  );
};

export default PrivateGallery;
